// Capture and replay of the inbound hose stream, so render performance can be
// measured against a fixed, repeatable workload instead of whatever the live
// estate happens to be doing.
//
// Messages are recorded *after* decompression, as plain strings. Replaying them
// as text frames therefore skips the LNC decode path entirely (processOne only
// decompresses ArrayBuffer frames), which means a tape stays valid regardless of
// what compression level the original session negotiated.

const MAX_RECORDED = 500000;

const AUTH_STATUS_MESSAGES = ["authok", "authfail", "unauth"];

const tape = [];
let recording = false;
let recordStart = 0;

export function startRecording() {
  tape.length = 0;
  recordStart = performance.now();
  recording = true;
  console.log("[tape] recording");
}

export function stopRecording() {
  recording = false;
  console.log("[tape] stopped,", tape.length, "messages");
  return tape.length;
}

export function record(edata) {
  if (!recording) return;
  if (tape.length >= MAX_RECORDED) {
    recording = false;
    console.warn("[tape] hit", MAX_RECORDED, "messages, stopped recording");
    return;
  }
  tape.push({ t: performance.now() - recordStart, d: edata });
}

export function dumpTape(filename = "borg-tape.ndjson") {
  const lines = tape.map((entry) => JSON.stringify(entry)).join("\n");
  const blob = new Blob([lines], { type: "application/x-ndjson" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  window.setTimeout(() => URL.revokeObjectURL(url), 10000);

  return tape.length;
}

function parseTape(text) {
  const entries = [];
  for (const line of text.split("\n")) {
    if (!line) continue;
    try {
      entries.push(JSON.parse(line));
    } catch (e) {
      console.warn("[tape] skipping unparseable line", line.substring(0, 80));
    }
  }
  return entries;
}

// Stands in for a WebSocket, exposing only the members protocol.js touches:
// binaryType, onopen/onmessage/onclose/onerror and send().
//
// Client commands other than `auth` are acknowledged but not acted on: a tape
// already contains the server's replies to the commands the *original* session
// sent. A focus or filter change during replay therefore still exercises the
// client-side layout and teardown cost, but will not reshape the universe.
export class TapeSocket {
  constructor(tapeUrl, options = {}) {
    this.binaryType = "blob";
    this.onopen = null;
    this.onmessage = null;
    this.onclose = null;
    this.onerror = null;

    this.rate = options.rate || 1;
    this.loop = !!options.loop;
    this.entries = null;
    this.timers = [];
    this.closed = false;

    fetch(tapeUrl)
      .then((res) => {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.text();
      })
      .then((text) => {
        this.entries = parseTape(text);
        console.log(
          "[tape] loaded",
          this.entries.length,
          "messages from",
          tapeUrl,
          "at",
          this.rate + "x"
        );
        if (this.onopen) this.onopen({});
      })
      .catch((err) => {
        console.error("[tape] failed to load", tapeUrl, err);
        if (this.onerror) this.onerror(err);
      });
  }

  send(command) {
    if (this.closed) return;

    // The client authenticates before anything else and will not proceed until
    // it sees authok.
    if (command.startsWith("auth ")) {
      this.deliver("authok", 0);
      this.scheduleTape();
    }
  }

  scheduleTape() {
    if (!this.entries) return;

    for (const entry of this.entries) {
      // The replay synthesises its own authok; replaying the recorded one would
      // re-enter the root object and duplicate its ~530 elements.
      if (AUTH_STATUS_MESSAGES.indexOf(entry.d) !== -1) continue;
      this.deliver(entry.d, entry.t / this.rate);
    }

    if (this.loop && this.entries.length) {
      const period = this.entries[this.entries.length - 1].t / this.rate;
      this.timers.push(
        window.setTimeout(() => {
          if (!this.closed) this.scheduleTape();
        }, period + 1000)
      );
    }
  }

  deliver(data, delayMs) {
    this.timers.push(
      window.setTimeout(() => {
        if (this.closed || !this.onmessage) return;
        this.onmessage({ data });
      }, delayMs)
    );
  }

  close() {
    this.closed = true;
    for (const timer of this.timers) window.clearTimeout(timer);
    this.timers.length = 0;
    if (this.onclose) this.onclose({});
  }
}

if (typeof window !== "undefined") {
  window.startRecording = startRecording;
  window.stopRecording = stopRecording;
  window.dumpTape = dumpTape;
}
